import React, { useEffect, useState } from 'react';
import { Check, Sparkles, Lock, Rocket } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api';
import { useAuth } from '@/context/AuthContext';
import { WrapulatorLogo } from './Brand';
import { TID } from '@/testIds';

export default function Membership() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState('monthly'); // monthly | yearly
  const [loading, setLoading] = useState(null); // lookup_key being processed

  useEffect(() => {
    api.get('/plans').then(({ data }) => setPlans(data.plans)).catch(() => toast.error('Could not load plans'));
  }, []);

  const startCheckout = async (lookup_key) => {
    setLoading(lookup_key);
    try {
      const { data } = await api.post('/payments/checkout', {
        lookup_key,
        origin_url: window.location.origin,
        ...(user?.email ? { email: user.email } : {}),
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout');
      setLoading(null);
    }
  };

  return (
    <section id="membership" className="relative py-24 lg:py-32 border-b mi-line overflow-hidden">
      <div className="absolute inset-0 mi-grid-lines opacity-30" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <WrapulatorLogo variant="primary" height={56}
                            className="mb-4 drop-shadow-[0_4px_14px_rgba(230,57,70,0.25)]" />
            <div className="mi-eyebrow mb-3 flex items-center gap-2 text-[#E63946]">
              <Sparkles className="w-3 h-3" /> // WRAPULATOR MEMBERSHIP
            </div>
            <h2 className="mi-headline text-5xl md:text-6xl lg:text-7xl">
              7 days free.<br />
              <span className="text-[#E63946]">Cancel any time.</span>
            </h2>
            <p className="mt-6 max-w-lg text-white/70 leading-relaxed">
              Unlock the pro-grade Wrapulator, branded PDF proposals, saved projects and fleet batch quoting.
              No card charged until day 8 — cancel from your Stripe portal any time before then.
            </p>
          </div>

          {/* Bill toggle */}
          <div
            className="inline-flex items-stretch border mi-line-strong"
            data-testid={TID.planBillToggle}
          >
            <button
              onClick={() => setBilling('monthly')}
              className={`px-5 py-3 mi-headline text-sm ${
                billing === 'monthly' ? 'bg-[#E63946] text-white' : 'bg-transparent text-white/70'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBilling('yearly')}
              className={`px-5 py-3 mi-headline text-sm ${
                billing === 'yearly' ? 'bg-[#E63946] text-white' : 'bg-transparent text-white/70'
              }`}
            >
              Yearly · save 20%
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {plans.map((p) => {
            const price = billing === 'monthly' ? p.monthly_price : p.yearly_price / 12;
            const lookup = billing === 'monthly' ? p.monthly_lookup : p.yearly_lookup;
            const highlight = p.highlight;
            return (
              <div
                key={p.id}
                data-testid={TID.planCard(p.id)}
                className={`relative p-8 border ${
                  highlight ? 'border-[#E63946] bg-[#E63946]/[0.03]' : 'mi-line-strong bg-[#0A0A0A]'
                }`}
              >
                {highlight && (
                  <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 bg-[#E63946] text-white px-3 py-1.5 font-mono-data uppercase tracking-widest text-[10px] font-bold">
                    <Rocket className="w-3 h-3" /> MOST POPULAR
                  </div>
                )}
                <div className="mi-eyebrow mb-2">{p.tagline}</div>
                <div className="mi-headline text-4xl mb-4">{p.name}</div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="mi-headline text-5xl">${price.toFixed(price < 10 ? 2 : 0)}</span>
                  <span className="text-white/50 text-sm">/ mo</span>
                </div>
                {billing === 'yearly' && (
                  <div className="text-xs mi-eyebrow text-[#E63946] mb-4">
                    ${p.yearly_price.toFixed(2)} billed yearly
                  </div>
                )}
                {billing !== 'yearly' && <div className="mb-4" />}

                <button
                  onClick={() => startCheckout(lookup)}
                  disabled={loading === lookup}
                  data-testid={billing === 'monthly' ? TID.planCtaMonthly(p.id) : TID.planCtaYearly(p.id)}
                  className={`w-full ${highlight ? 'mi-btn-red' : 'mi-btn-ghost'} disabled:opacity-60`}
                >
                  {loading === lookup ? 'Starting…' : 'Start 7-day free trial'}
                </button>

                <ul className="mt-8 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-white/85">
                      <Check className="w-4 h-4 text-[#E63946] mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-6 text-xs mi-eyebrow text-white/50">
          <span className="flex items-center gap-2"><Lock className="w-3 h-3" /> SECURED BY STRIPE</span>
          <span>NO CARD CHARGED UNTIL DAY 8</span>
          <span>CANCEL ANYTIME</span>
        </div>
      </div>
    </section>
  );
}
